/***
 *
 *   PRODUCT EVENTS — DATE RANGE
 *   Shared by the list and the per-event drill-down. The selected range lives
 *   in the URL (?preset=&start=&end=) so it survives navigation, Back and
 *   deep links (CORE-4277).
 *
 **********/

import { useEffect, useState, useRef, useCallback } from 'react';
import { Icon } from 'components/lib';
import Style from './tailwind';
import { DATE_PRESETS, DEFAULT_DATE_PRESET, MSG } from './constants';

// ─── Date helpers ────────────────────────────────────────────────────────────

export function parseDateOnly(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function formatDate(d) {
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function getDateRange(preset) {
  const found = DATE_PRESETS.find(p => p.key === preset);
  if (!found) return null;
  const { start, end } = found.getRange();
  return { start: formatDate(start), end: formatDate(end), label: found.label };
}

// A real calendar date in YYYY-MM-DD form (rejects 2026-02-30 etc.).
export function isIsoDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return formatDate(parseDateOnly(value)) === value;
}

export function customRange(start, end) {
  return { start, end, label: `${start} \u2014 ${end}`, preset: 'custom' };
}

export function defaultDateRange() {
  return { ...getDateRange(DEFAULT_DATE_PRESET), preset: DEFAULT_DATE_PRESET };
}

// ─── URL (de)serialisation ───────────────────────────────────────────────────

// Presets are recomputed against today, so a "Last 7 days" link stays relative.
// A custom range needs two valid dates in order; anything else falls back to
// the default rather than querying a nonsense range.
export function rangeFromSearch(searchParams) {
  const preset = searchParams.get('preset');
  if (preset && preset !== 'custom') {
    const range = getDateRange(preset);
    return range ? { ...range, preset } : defaultDateRange();
  }
  const start = searchParams.get('start');
  const end   = searchParams.get('end');
  if (preset === 'custom' && isIsoDate(start) && isIsoDate(end) && start <= end) {
    return customRange(start, end);
  }
  return defaultDateRange();
}

export function rangeToSearch(range) {
  const params = new URLSearchParams({ preset: range.preset });
  if (range.preset === 'custom') {
    params.set('start', range.start);
    params.set('end', range.end);
  }
  return params.toString();
}

export function eventDetailPath(name, range) {
  return `/product-events/${encodeURIComponent(name)}?${rangeToSearch(range)}`;
}

// ─── Date Range Selector ─────────────────────────────────────────────────────

export function DateRangeSelector({ value, onChange }) {
  const [open, setOpen]       = useState(false);
  const [customFrom, setFrom] = useState('');
  const [customTo, setTo]     = useState('');
  const ref                   = useRef(null);

  useEffect(() => {
    if (value?.preset === 'custom') {
      setFrom(value.start ?? '');
      setTo(value.end ?? '');
      return;
    }
    setFrom('');
    setTo('');
  }, [value?.preset, value?.start, value?.end]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectPreset = useCallback((key) => {
    const range = getDateRange(key);
    if (range) {
      onChange({ start: range.start, end: range.end, label: range.label, preset: key });
      setOpen(false);
    }
  }, [onChange]);

  const isCustomRangeValid = customFrom && customTo && customFrom <= customTo;

  const applyCustom = useCallback(() => {
    if (isCustomRangeValid) {
      onChange(customRange(customFrom, customTo));
      setOpen(false);
    }
  }, [customFrom, customTo, isCustomRangeValid, onChange]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`${Style.btnBase} ${Style.btnInactive}`}
      >
        <Icon image="calendar" size={14} />
        {value.label}
        <Icon image="chevron-down" size={12} />
      </button>

      {open && (
        <div className={Style.dateDropdown}>
          {DATE_PRESETS.map(({ key, label }) => (
            <button
              type="button"
              key={key}
              onClick={() => selectPreset(key)}
              className={`${Style.presetBtn} ${value.preset === key ? Style.presetActive : Style.presetInactive}`}
            >
              {label}
            </button>
          ))}

          <div className={Style.customSection}>
            <div className={Style.customLabel}>{MSG.customRange}</div>
            <div className={Style.customInputRow}>
              <input type="date" aria-label="Start date" value={customFrom} onChange={e => setFrom(e.target.value)} className={Style.dateInput} />
              <input type="date" aria-label="End date"   value={customTo}   onChange={e => setTo(e.target.value)}   className={Style.dateInput} />
            </div>
            <button
              type="button"
              onClick={applyCustom}
              disabled={!isCustomRangeValid}
              className={`${Style.applyBase} ${isCustomRangeValid ? Style.applyEnabled : Style.applyDisabled}`}
            >
              {MSG.apply}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
